import React, { ChangeEvent, Component, Fragment, MouseEvent } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { MutationResult } from 'react-apollo';
import { DataProxy } from 'apollo-cache';
import { compose } from 'recompose';
import { FormikProps } from 'formik';
import { Grid, IconButton, InputAdornment, Switch } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import MoreHorzIcon from '@material-ui/icons/MoreHoriz';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import { ClientGroupSource, ContentApproval, SourceStatus } from 'api';
import { Alert } from 'components';
import { clientGroupSourceUpdateResponse } from 'apollo/responses';

import Input from './Input';
import Menu from './Menu';
import { IMutationsRenderProps } from './Mutations';

interface IProps {
  mutations: IMutationsRenderProps;
  formikProps: FormikProps<ClientGroupSource>;
  onDelete: () => void;
  onAdd: (cache: DataProxy, result: MutationResult) => void;
  onSettingsChange: (cache: DataProxy, result: MutationResult) => void;
}

interface IPropsEnhanced extends IProps, WithStyles<typeof styles>, WithTranslation {}

interface IState {
  anchorEl: HTMLElement;
  deleteOpen: boolean;
}

const styles = () =>
  createStyles({
    deleteIcon: {
      transform: 'rotate(45deg)',
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(withStyles(styles), withTranslation());

class Source extends Component<IPropsEnhanced, IState> {
  state: IState = {
    anchorEl: null,
    deleteOpen: false,
  };

  toggleConfirm = () => {
    const { formikProps } = this.props;
    const { values } = formikProps;

    if (!values.id) {
      return this.handleDelete();
    }

    const { deleteOpen } = this.state;
    this.setState({ deleteOpen: !deleteOpen });
  };

  handleSave = () => {
    const { mutations, formikProps, onAdd } = this.props;
    const { createSource } = mutations;
    const { values } = formikProps;

    values.source_value = values.source_value.trim();

    return createSource.mutation({
      variables: values,
      update: onAdd,
    });
  };

  handleDelete = () => {
    const { mutations, formikProps, onDelete } = this.props;
    const { deleteSource } = mutations;
    const { values } = formikProps;

    if (!values.id) {
      return onDelete();
    }

    return deleteSource.mutation({
      variables: { id: values.id },
      update: onDelete,
    });
  };

  handleApproveChange = (approval: ContentApproval) => {
    const { mutations, formikProps, onSettingsChange } = this.props;
    const { updateSource } = mutations;
    const { values } = formikProps;

    return updateSource.mutation({
      variables: { data: { id: values.id, approval } },
      update: onSettingsChange,
      optimisticResponse: clientGroupSourceUpdateResponse({ ...values, approval }),
    });
  };

  handleStatusChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { mutations, formikProps, onSettingsChange } = this.props;
    const { updateSource } = mutations;
    const { values } = formikProps;

    const status = {
      source_status: event.target.checked ? SourceStatus.Active : SourceStatus.Disabled,
    };

    return updateSource.mutation({
      variables: { data: { id: values.id, ...status } },
      update: onSettingsChange,
      optimisticResponse: clientGroupSourceUpdateResponse({ ...values, ...status }),
    });
  };

  handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleMenuClose = () => {
    this.setState({ anchorEl: null });
  };

  renderStatusAdornment = () => {
    const { formikProps } = this.props;
    const { values } = formikProps;

    return (
      <InputAdornment variant="filled" position="end">
        <Switch
          checked={values.source_status === SourceStatus.Active}
          onChange={this.handleStatusChange}
        />
      </InputAdornment>
    );
  };

  renderSettingsAdornment = () => (
    <InputAdornment variant="filled" position="end">
      <IconButton onClick={this.handleMenuClick}>
        <MoreHorzIcon />
      </IconButton>
    </InputAdornment>
  );

  renderDelete() {
    const { classes } = this.props;

    return (
      <Grid item>
        <IconButton onClick={this.toggleConfirm}>
          <AddCircleIcon className={classes.deleteIcon} />
        </IconButton>
      </Grid>
    );
  }

  renderMenu() {
    const { formikProps } = this.props;
    const { anchorEl } = this.state;

    return (
      <Menu
        formikProps={formikProps}
        onApproveChange={this.handleApproveChange}
        onClose={this.handleMenuClose}
        anchorEl={anchorEl}
      />
    );
  }

  renderAlert() {
    const { t } = this.props;
    const { deleteOpen } = this.state;

    return (
      <Alert
        open={deleteOpen}
        onConfirm={() => {
          this.toggleConfirm();
          this.handleDelete();
        }}
        onCancel={this.toggleConfirm}
        message={t('groups:deleteSourceAlert')}
        confirm={t('common:delete')}
      />
    );
  }

  render() {
    const { formikProps } = this.props;
    const { values } = formikProps;
    const statusAdornment = this.renderStatusAdornment();
    const settingsAdornment = this.renderSettingsAdornment();

    return (
      <Fragment>
        <Grid container alignItems="center">
          <Grid item xs>
            <Input
              formikProps={formikProps}
              onSave={this.handleSave}
              statusAdornment={!!values.id && statusAdornment}
              settingsAdornment={settingsAdornment}
            />
          </Grid>
          {this.renderDelete()}
        </Grid>
        {this.renderAlert()}
        {this.renderMenu()}
      </Fragment>
    );
  }
}

export default enhancers(Source);
